import { useState, useEffect, useRef, useCallback } from 'react';
import { apiCall, PermissionDeniedError } from '../services/api';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface SearchResult {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  isImage: boolean;
  isVideo: boolean;
  storageClass?: string;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
  capped: boolean;
  timedOut?: boolean;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

interface UseSearchReturn {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  results: SearchResult[];
  isLoading: boolean;
  isSearchMode: boolean;
  isCapped: boolean;
  isTimedOut: boolean;
  error: string | null;
  clearSearch: () => void;
}

// ---------------------------------------------------------------------------
// useSearch
// ---------------------------------------------------------------------------

export function useSearch(bucketName: string, scope: string | null): UseSearchReturn {
  // State (drives re-renders)
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCapped, setIsCapped] = useState<boolean>(false);
  const [isTimedOut, setIsTimedOut] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Refs (stable, do not drive re-renders)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Generation counter: incremented on each new search dispatch and on cleanup.
  // When a response arrives, we compare its generation to the current ref value.
  // If they don't match, the response is stale and is silently discarded.
  // This is the correct approach given apiCall does not accept AbortSignal.
  const searchGenRef = useRef<number>(0);

  // Derived — pure computation, not state
  const isSearchMode = query.length >= 2;

  // -------------------------------------------------------------------------
  // Core effect: debounce + stale-response guard
  // -------------------------------------------------------------------------
  useEffect(() => {
    // Clear previous debounce timer on every run
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (query.length < 2) {
      // Reset all search state when dropping below threshold
      setResults([]);
      setError(null);
      setIsLoading(false);
      setIsCapped(false);
      setIsTimedOut(false);
      return;
    }

    // Arm new 300 ms debounce timer
    debounceRef.current = setTimeout(async () => {
      // Increment generation — any in-flight response from a previous gen is discarded
      const currentGen = ++searchGenRef.current;

      setIsLoading(true);
      setError(null);

      const scopePath = scope
        ? '/' + scope.split('/').map(s => encodeURIComponent(s)).join('/')
        : '';
      const endpoint = `/s3/search/${bucketName}${scopePath}?q=${encodeURIComponent(query)}`;

      try {
        const data = (await apiCall(endpoint)) as SearchResponse;

        // Stale-response guard: discard if a newer search was dispatched
        if (searchGenRef.current !== currentGen) return;

        setResults(data.results ?? []);
        setIsCapped(data.capped ?? false);
        setIsTimedOut(data.timedOut ?? false);
      } catch (err: unknown) {
        if (searchGenRef.current !== currentGen) return;

        if (err instanceof PermissionDeniedError) {
          setError(err.message);
        } else {
          setError(
            err instanceof Error ? err.message : 'Search failed. Please try again.'
          );
        }
      } finally {
        // Only update loading state if this is still the current generation
        if (searchGenRef.current === currentGen) {
          setIsLoading(false);
        }
      }
    }, 300);

    // Cleanup: clear debounce timer and increment gen to discard any in-flight response
    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      searchGenRef.current++;
    };
  }, [query, bucketName, scope]);

  // -------------------------------------------------------------------------
  // clearSearch
  // -------------------------------------------------------------------------
  const clearSearch = useCallback(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    searchGenRef.current++; // discard any in-flight response
    setQuery('');
    setResults([]);
    setError(null);
    setIsLoading(false);
    setIsCapped(false);
    setIsTimedOut(false);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    isSearchMode,
    isCapped,
    isTimedOut,
    error,
    clearSearch,
  };
}
